"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

export interface HeroSlide {
  id: string;
  nasPath: string;
  brandName: string;
  brandSlug: string;
  showSlug: string;
  season: string;
  year: number;
}

const BP = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

export default function HeroImage({ slides }: { slides: HeroSlide[] }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % slides.length);
        setVisible(true);
      }, 600);
    }, 6600); // matches animation duration
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div style={{
        height: "78vh",
        background: "var(--paper-2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid var(--ink)",
      }}>
        <p style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--ink-3)" }}>
          The archive awaits.
        </p>
      </div>
    );
  }

  const slide = slides[idx];
  // Alternate pan direction per slide so consecutive images feel distinct
  const kbClass = idx % 2 === 0 ? "kb-down" : "kb-up";

  return (
    <div style={{ position: "relative", height: "78vh", overflow: "hidden", borderBottom: "1px solid var(--ink)" }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        key={slide.id}
        src={`${BP}/api/thumb/${slide.nasPath}?w=1600`}
        alt=""
        className={kbClass}
        style={{
          position: "absolute", inset: 0,
          width: "100%", height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.6s ease",
          transformOrigin: "center center",
        }}
      />

      {/* Bottom gradient */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "45%",
        background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)",
        pointerEvents: "none",
      }} />

      {/* Caption + dots */}
      <div className="hero-caption" style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "20px 40px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}>
        {/* Top row: label + link */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <span className="hero-label" style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)",
          }}>
            Wunderlook Archive
          </span>
          <Link
            href={`/archive/${slide.brandSlug}/${slide.showSlug}`}
            className="hero-show-link"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
              textDecoration: "none",
              borderBottom: "1px solid rgba(255,255,255,0.3)",
              paddingBottom: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "70vw",
            }}
          >
            {slide.brandName} · {slide.season} {slide.year} →
          </Link>
        </div>

        {/* Dot indicators */}
        {slides.length > 1 && (
          <div style={{ display: "flex", gap: 6 }}>
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => { setVisible(false); setTimeout(() => { setIdx(i); setVisible(true); }, 300); }}
                style={{
                  width: i === idx ? 20 : 6,
                  height: 6,
                  background: i === idx ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "width 0.3s ease, background 0.3s ease",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
